import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Message } from "../../utils/types";
import { useHotspots } from "../hotspots";
import { useWorkspaces } from "../workspaces";

interface Props {}

interface MessageMap {
  [hotspotId: string]: Message[];
}

export type ContextValue = {
  messages: MessageMap;
  myMessages: MessageMap;
  fetchMessages: (currentHotspotId: string) => Promise<void>;
  fetchMyMessages: (currentHotspotId: string) => Promise<void>;
  hostMessages: MessageMap;
  fetchContactMessages: (contactId: string) => Promise<void>;
  addMessage: (
    message: string,
    currentHotspotId: string | undefined
  ) => Promise<ApiResponse<Message> | undefined>;
  addContactMessage: (
    message: string,
    contactId: string | undefined
  ) => Promise<ApiResponse<Message> | undefined>;
};

export const MessagesContext = React.createContext<ContextValue | undefined>(
  undefined
);

export const MessagesProvider: React.FC<Props> = (props) => {
  const { currentWorkspaceId } = useWorkspaces();
  const { currentHotspot } = useHotspots();
  const [messages, setMessages] = useState<MessageMap>({});
  const [hostMessages, setHostMessages] = useState<MessageMap>({});
  const [myMessages, setMyMessages] = useState<MessageMap>({});

  const fetchMessages = useCallback(
    async (currentHotspotId: string) => {
      if (!currentWorkspaceId || !currentHotspotId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/hotspot/${currentHotspotId}/messages`
      );

      const apiResponse: ApiResponse<Message[]> = await response.json();

      setMessages((prevState) => ({
        ...prevState,
        [currentHotspotId]: apiResponse.data || []
      }));
    },
    [currentWorkspaceId]
  );

  const fetchMyMessages = useCallback(
    async (currentHotspotId: string) => {
      if (!currentWorkspaceId || !currentHotspotId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/hotspot/${currentHotspotId}/my-messages`
      );

      const apiResponse: ApiResponse<Message[]> = await response.json();

      setMyMessages((prevState) => ({
        ...prevState,
        [currentHotspotId]: apiResponse.data || []
      }));
    },
    [currentWorkspaceId]
  );

  useEffect(() => {
    if (!currentHotspot?.id || !currentWorkspaceId) return;

    if (
      !messages[currentHotspot.id] &&
      currentHotspot.verified_by === currentWorkspaceId
    ) {
      fetchMessages(currentHotspot.id);
    }
  }, [
    currentHotspot?.id,
    currentHotspot?.verified_by,
    currentWorkspaceId,
    fetchMessages,
    messages
  ]);

  useEffect(() => {
    if (!currentHotspot?.id || !currentWorkspaceId) return;

    if (!myMessages[currentHotspot.id]) {
      fetchMyMessages(currentHotspot.id);
    }
  }, [currentHotspot?.id, currentWorkspaceId, fetchMyMessages, myMessages]);

  const addMessage = useCallback(
    async (message: string, currentHotspotId: string | undefined) => {
      if (!currentHotspotId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/hotspot/${currentHotspotId}/messages`,
          {
            method: "POST",
            body: JSON.stringify({ message })
          }
        );

        const apiResponse: ApiResponse<Message> = await response.json();

        await fetchMyMessages(currentHotspotId);

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchMyMessages]
  );

  const fetchContactMessages = useCallback(
    async (contactId: string) => {
      if (!contactId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/contact/${contactId}/messages`
      );

      const apiResponse: ApiResponse<Message[]> = await response.json();

      if (apiResponse.status) {
        setHostMessages((prevState) => ({
          ...prevState,
          [contactId]: apiResponse.data
        }));
      }
    },
    [currentWorkspaceId]
  );

  const addContactMessage = useCallback(
    async (message: string, contactId: string | undefined) => {
      if (!contactId) return;

      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/contact/${contactId}/messages`,
          {
            method: "POST",
            body: JSON.stringify({ message })
          }
        );

        const apiResponse: ApiResponse<Message> = await response.json();

        await fetchContactMessages(contactId);

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchContactMessages]
  );

  const value = useMemo(
    () => ({
      messages,
      hostMessages,
      myMessages,
      fetchMessages,
      fetchMyMessages,
      fetchContactMessages,
      addMessage,
      addContactMessage
    }),
    [
      messages,
      myMessages,
      fetchMessages,
      fetchMyMessages,
      addMessage,
      addContactMessage,
      fetchContactMessages,
      hostMessages
    ]
  );

  return <MessagesContext.Provider value={value} {...props} />;
};

export const useMessages = (): ContextValue => {
  const context = useContext(MessagesContext);

  if (context === undefined) {
    throw new Error("useMessages must be used within an MessagesProvider");
  }

  return context;
};
